import { Divider, Group, Input, SegmentedControl, Select, Text } from '@byecode/ui'
import type { AvailableFieldBlockInfo } from '@lighthouse/core'
import { ListItem4ByecodeUi } from '@lighthouse/shared'
import { useAtomValue } from 'jotai'
import React, { useDeferredValue, useMemo, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { availableFieldBlocksAtom } from '@/atoms/page/state'
import { AnchorSetting } from '@/components/AnchorSetting'
import { getBlockAndPageDesignLimit, PositionSetting } from '@/components/StyleSetting'
import AnimationSetting from '@/components/StyleSetting/AnimationSetting'
import { SizeSetting } from '@/components/StyleSetting/SizeSetting'
import { VisibilitySetting } from '@/components/VisibilitySetting'

import { BlockConfigureSegmentedControl } from '../Common/BlockConfigureSegmentedControl'
import { CursorSetting } from '../Common/CursorSetting'
import type { BlockSettingsSegmentedEnum } from '../constants'
import { BLOCK_SETTINGS_SEGMENTED_ENUM } from '../constants'

const MAX_DEPTH_OPTIONS = [
    { label: 'H1', value: '1' },
    { label: 'H1-H2', value: '2' },
    { label: 'H1-H3', value: '3' }
]

/**
 * 将 blockId + scope 编码为 Select 的单一 value 字符串。
 * 格式与 getWithScopeId 一致：scope ? `${scope}@${blockId}` : blockId
 */
function encodeSelectValue(blockId: string, scope?: string): string {
    return scope ? `${scope}@${blockId}` : blockId
}

/**
 * 将 Select value 解码为 { blockId, scope }。
 * scope 本身是一个 blockId（不含 '@'），因此只需找第一个 '@' 分割。
 */
function decodeSelectValue(value: string): { blockId: string; scope?: string } {
    const atIndex = value.indexOf('@')
    if (atIndex < 0) return { blockId: value }
    return { scope: value.slice(0, atIndex), blockId: value.slice(atIndex + 1) }
}

export const TocBlockSetting: React.FC = () => {
    const { control, watch, setValue } = useFormContext()
    const [currentTab, setCurrentTab] = useState<BlockSettingsSegmentedEnum>(BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN)
    const styleProps = getBlockAndPageDesignLimit('toc')

    const [targetBlockId, targetScope] = watch(['targetBlockId', 'targetScope'])

    const availableFieldBlocksInfo = useDeferredValue(useAtomValue(availableFieldBlocksAtom))

    /**
     * Block 可见性 / 可引用规则（TocBlock 笔记绑定）
     *
     * ── 索引范围 ──
     * 1. 仅限「当前 page stack 栈顶页面」，不支持跨页面引用。
     *
     * ── Scope（同步组件实例） ──
     * 2. scope = 页面树上同步实例根的 block id；undefined 表示普通页面 block。
     * 3. availableFieldBlocksAtom 不限制 scope（同页多实例均可出现），
     *    存储时必须同时保存 scope，运行时据此在 pageBlocks vs syncComponents 中查找。
     *
     * ── CustomView ──
     * 4. 在 CustomView A 内：可见 A 内 block + 页面公共区域 block。
     * 5. 在 CustomView A 内：不可见 CustomView B 内 block。
     * 6. 在页面公共区域：不可见任何 CustomView 内的 block。
     *
     * ── 多面板 Container ──
     * 7. 仅包含 container 当前激活面板（currentView）上的 block。
     */
    const notesBlockOptions = useMemo(() => {
        return availableFieldBlocksInfo
            .filter((ctx: AvailableFieldBlockInfo) => (ctx.block.config as { inputType?: string })?.inputType === 'notes')
            .map((ctx: AvailableFieldBlockInfo) => ({
                label: ctx.block.title || '未命名笔记',
                value: encodeSelectValue(ctx.block.id, ctx.scope)
            }))
    }, [availableFieldBlocksInfo])

    const selectedValue = targetBlockId ? encodeSelectValue(targetBlockId, targetScope) : undefined

    const handleBlockChange = (val: string | null | undefined) => {
        if (!val) {
            setValue('targetBlockId', '')
            setValue('targetScope', undefined)
            return
        }
        const { blockId, scope } = decodeSelectValue(val)
        setValue('targetBlockId', blockId)
        setValue('targetScope', scope)
    }

    return (
        <>
            <BlockConfigureSegmentedControl value={currentTab} onChange={setCurrentTab} />

            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN && (
                <>
                    <PositionSetting {...styleProps.position} />
                    <Divider />
                    <SizeSetting {...styleProps.size} />
                    <Divider />
                    <Controller control={control} name="cursor" render={({ field }) => <CursorSetting {...field} />} />
                </>
            )}
            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.CONFIGURE && (
                <>
                    <Group label="目录配置">
                        <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                            <Text>绑定笔记</Text>
                            <div style={{ width: 180 }}>
                                <Select
                                    style={{ width: '100%' }}
                                    placeholder="请选择笔记输入框"
                                    clearable
                                    value={selectedValue}
                                    options={notesBlockOptions}
                                    onChange={handleBlockChange}
                                />
                            </div>
                        </ListItem4ByecodeUi>
                        <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                            <Text>标题层级</Text>
                            <div style={{ width: 180 }}>
                                <SegmentedControl
                                    style={{ width: '100%' }}
                                    fullWidth
                                    data={MAX_DEPTH_OPTIONS}
                                    value={String(watch('maxDepth') ?? 3)}
                                    onChange={val => setValue('maxDepth', Number(val))}
                                />
                            </div>
                        </ListItem4ByecodeUi>
                        <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                            <Text>垂直偏移量</Text>
                            <Input
                                style={{ width: 180 }}
                                type="number"
                                suffix="px"
                                value={watch('offsetY') ?? 0}
                                onChange={event => setValue('offsetY', Number(event.target.value))}
                            />
                        </ListItem4ByecodeUi>
                    </Group>
                    <Divider />
                    <AnchorSetting />
                    <Divider />
                    <VisibilitySetting />
                </>
            )}

            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.INTERACTION && <AnimationSetting />}
        </>
    )
}
